import { defineStore } from 'pinia';
import moment from 'moment';
import { handleRequest } from '@/composables';
import { RequestMethodEnum } from '@/types/enums/RequestMethodEnum';
import type {
    ExternalApiUsageStateType,
    ExternalApiUsageGettersType,
    ExternalApiUsageActionsType,
} from '@/types/stores/ExternalApiUsageStoreTypes';
import type {
    ExternalApiUsageFilterType,
    ExternalApiUsageDataType,
    ExternalApiUsageGroupType,
} from '@/types/statistics/ExternalApiUsageType';
import type { FilterChipType } from '@/types/statistics/FilterChipType';
import type { LineChartDataType } from '@/types/ChartType';
import {
    formatDateRangeChip, createEndpointFilterChip, buildStatisticsQueryParams,
} from '@/utils/storeUtils';
import { getIntelligentChartColors } from '@/utils/chartColorUtils';
import { buildQueryString } from '@/utils/queryUtils';
import i18n from '@/plugins/i18n';
import { VDataTableServer } from 'vuetify/components';
import type { AxiosResponse } from 'axios';

const createDefaultFilters = (): ExternalApiUsageFilterType => ({
    dateRange: {
        start: moment().subtract(7, 'days').startOf('day').toDate(),
        end: moment().endOf('day').toDate(),
    },
    groupedBy: 'day',
    endpoints: undefined,
});

const createDefaultData = (): ExternalApiUsageDataType => ({
    groupedBy: 'day',
    startDate: '',
    endDate: '',
    endpoints: undefined,
    availableEndpoints: [],
    summary: {
        total_requests: 0,
        unique_endpoints: 0,
        average_response_time: 0,
        error_rate: 0,
        most_used_endpoint: '',
    },
    groups: [],
});

const createDefaultHeaders = (): VDataTableServer['$props']['headers'] => [
    {
        title: 'statistics.api.table.period',
        key: 'type',
        sortable: true,
    },
    {
        title: 'statistics.api.table.totalRequests',
        key: 'total_requests',
        sortable: true,
        align: 'end',
    },
    {
        title: 'statistics.api.table.successfulRequests',
        key: 'successful_requests',
        sortable: true,
        align: 'end',
    },
    {
        title: 'statistics.api.table.failedRequests',
        key: 'failed_requests',
        sortable: true,
        align: 'end',
    },
];

export const useExternalApiUsageStore = defineStore<
    'externalApiUsage',
    ExternalApiUsageStateType,
    ExternalApiUsageGettersType,
    ExternalApiUsageActionsType
>('externalApiUsage', {
    state: (): ExternalApiUsageStateType => ({
        isLoading: false,
        filters: createDefaultFilters(),
        data: createDefaultData(),
        headers: createDefaultHeaders(),
        cachedEndpoints: [],
    }),

    getters: {
        getFilteredData: state => state.data,
        getTotalRequests: state => state.data.summary.total_requests,
        hasData: state => (state.data?.groups?.length || 0) > 0,
        getFilterChips: (state): FilterChipType[] => {
            const chips: FilterChipType[] = [];
            const { t } = i18n.global;

            // Always show grouping as first chip
            chips.push({
                key: 'groupedBy',
                label: `${t('common.groupedBy')}: ${t(`common.grouping.${state.filters.groupedBy}`)}`,
                value: state.filters.groupedBy,
                icon: 'mdi-group',
                removable: false,
            });

            if (state.filters.dateRange?.start && state.filters.dateRange?.end) {
                chips.push(formatDateRangeChip(state.filters.dateRange));
            }

            const endpointChip = createEndpointFilterChip(state.filters.endpoints || null, state.data?.groups || []);
            if (endpointChip) {
                chips.push(endpointChip);
            }

            return chips;
        },
        getTranslatedHeaders: state => {
            const { t } = i18n.global;

            return (
                state.headers?.map(header => ({
                    ...header,
                    title: header.title ? t(header.title) : header.title,
                })) || []
            );
        },
        getEndpointOptions: (state: ExternalApiUsageStateType) => {
            return (state.cachedEndpoints || []).map(endpoint => ({
                title: endpoint,
                value: endpoint,
            }));
        },
        getChartData:
            state =>
                (metric: 'total_requests' | 'successful_requests' | 'failed_requests'): LineChartDataType => {
                    if (!state.data?.groups?.length) {
                        return { labels: [], datasets: [] };
                    }

                    const labels = state.data.groups.map(group => group.label);

                    const endpointMap = new Map<string, { data: number[] }>();

                    for (const group of state.data.groups) {
                        for (const endpoint of group.data) {
                            if (!endpointMap.has(endpoint.endpoint)) {
                                endpointMap.set(endpoint.endpoint, { data: [] });
                            }
                            const endpointData = endpointMap.get(endpoint.endpoint)!;
                            endpointData.data.push(endpoint[metric]);
                        }
                    }

                    const endpointEntries = Array.from(endpointMap.entries());
                    const colorPairs = getIntelligentChartColors(endpointEntries.length);

                    const datasets = endpointEntries.map(([endpoint, endpointInfo], index) => {
                        const colorPair = colorPairs[index];

                        return {
                            label: endpoint,
                            data: endpointInfo.data,
                            borderColor: colorPair.borderColor,
                            backgroundColor: colorPair.backgroundColor,
                            tension: 0.3,
                            fill: false,
                        };
                    });

                    return { labels, datasets };
                },
    },

    actions: {
        extractEndpointsFromGroups(groups: ExternalApiUsageGroupType[]): string[] {
            const uniqueEndpoints = new Set<string>();
            for (const group of groups) {
                for (const item of group.data) {
                    uniqueEndpoints.add(item.endpoint);
                }
            }

            return Array.from(uniqueEndpoints).sort();
        },
        async fetchData() {
            this.isLoading = true;

            if (this.cachedEndpoints.length === 0) {
                await this.loadAvailableEndpoints();
            }
            const queryParams = buildStatisticsQueryParams(this.filters);

            if (this.filters.endpoints && this.filters.endpoints.length > 0) {
                queryParams.endpoints = this.filters.endpoints;
            }
            const query = buildQueryString(queryParams);

            await handleRequest({
                method: RequestMethodEnum.Get,
                endpoint: `/logs/statistics/external-api/requests?${query}`,
                onSuccess: (response: AxiosResponse<ExternalApiUsageDataType>) => {
                    this.data = response.data;

                    if (!this.filters.endpoints || this.filters.endpoints.length === 0) {
                        this.cachedEndpoints = this.extractEndpointsFromGroups(response.data.groups);
                    }
                },
            });
            this.isLoading = false;
        },
        async updateFilters(newFilters: Partial<ExternalApiUsageFilterType>) {
            this.filters = { ...this.filters, ...newFilters };
            await this.fetchData();
        },

        resetFilters() {
            this.filters = createDefaultFilters();
        },

        async loadAvailableEndpoints() {
            const tempFilters = { ...this.filters, endpoints: null };
            const queryParams = buildStatisticsQueryParams(tempFilters);
            const query = buildQueryString(queryParams);

            await handleRequest({
                method: RequestMethodEnum.Get,
                endpoint: `/logs/statistics/external-api/requests?${query}`,
                onSuccess: (response: AxiosResponse<ExternalApiUsageDataType>) => {
                    this.cachedEndpoints = this.extractEndpointsFromGroups(response.data.groups);
                },
            });
        },

        async removeFilter(filterKey: string) {
            const defaultFilters = createDefaultFilters();

            switch (filterKey) {
            case 'dateRange':
                this.filters.dateRange = defaultFilters.dateRange;
                break;
            case 'groupedBy':
                this.filters.groupedBy = defaultFilters.groupedBy;
                break;
            case 'endpoints':
                this.filters.endpoints = undefined;
                break;
            default:
                console.warn(`Unknown filter key: ${filterKey}`);

                return;
            }

            await this.fetchData();
        },
    },
});
